import React from 'react';
import { interestClusterSubjectFamily } from '../../utils/interestClusterDisplay';
import { getInterestFamilyTheme } from '../../utils/interestTheme';

/**
 * Cute flat 2D SVG illustrations for interest cluster pop-ups (one per subject family).
 * @param {{ cluster: { cluster_id?: number }, className?: string }} props
 */
function InterestClusterIllustration({ cluster, className = '' }) {
  const family = interestClusterSubjectFamily(cluster);
  const theme = getInterestFamilyTheme(family);
  const { surface, ink, inkMuted, accent } = theme;
  const shared = `w-full h-full max-h-[200px] max-w-[280px] ${className}`;

  switch (family) {
    case 'business':
      return (
        <svg className={shared} viewBox="0 0 280 200" fill="none" aria-hidden>
          <rect x="40" y="120" width="200" height="56" rx="16" fill={surface} />
          <rect x="56" y="88" width="72" height="48" rx="12" fill={surface} />
          <rect x="152" y="72" width="72" height="64" rx="12" fill={surface} />
          <rect x="64" y="136" width="40" height="28" rx="6" fill={accent} fillOpacity="0.2" />
          <rect x="120" y="136" width="96" height="8" rx="4" fill={inkMuted} fillOpacity="0.35" />
          <rect x="120" y="152" width="72" height="8" rx="4" fill={inkMuted} fillOpacity="0.25" />
          <path d="M168 48 L192 72 L144 72 Z" fill={accent} fillOpacity="0.35" />
          <circle cx="92" cy="108" r="8" fill={accent} />
          <path d="M72 56 h40 M92 56 v24" stroke={ink} strokeWidth="3" strokeLinecap="round" />
          <circle cx="188" cy="100" r="6" fill={accent} fillOpacity="0.5" />
        </svg>
      );

    case 'technology':
      return (
        <svg className={shared} viewBox="0 0 280 200" fill="none" aria-hidden>
          <rect x="48" y="48" width="184" height="112" rx="16" fill={surface} />
          <rect x="64" y="64" width="152" height="80" rx="8" fill={inkMuted} fillOpacity="0.12" />
          <text x="88" y="100" fill={accent} fontSize="18" fontWeight="700" fontFamily="monospace">
            {'{ }'}
          </text>
          <text x="128" y="118" fill={ink} fontSize="14" fontWeight="600" fontFamily="monospace">
            AI
          </text>
          <rect x="108" y="168" width="64" height="8" rx="4" fill={surface} />
          <circle cx="76" cy="88" r="6" fill={accent} fillOpacity="0.6" />
          <circle cx="204" cy="76" r="4" fill={accent} />
          <path d="M180 108 h32 M196 92 v32" stroke={inkMuted} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );

    case 'engineering':
      return (
        <svg className={shared} viewBox="0 0 280 200" fill="none" aria-hidden>
          <path d="M40 140 h200" stroke={inkMuted} strokeWidth="4" strokeLinecap="round" />
          <path d="M60 140 V100 h40 v40 M100 140 V80 h40 v60 M140 140 V96 h40 v44 M180 140 V72 h40 v68" fill={surface} stroke={ink} strokeWidth="2" />
          <circle cx="140" cy="56" r="28" fill={surface} stroke={ink} strokeWidth="3" />
          <circle cx="140" cy="56" r="10" fill={accent} fillOpacity="0.4" />
          <path d="M140 28 v12 M140 72 v12 M112 56 h12 M156 56 h12" stroke={inkMuted} strokeWidth="2.5" strokeLinecap="round" />
          <rect x="200" y="108" width="48" height="32" rx="8" fill={accent} fillOpacity="0.25" />
        </svg>
      );

    case 'creative':
      return (
        <svg className={shared} viewBox="0 0 280 200" fill="none" aria-hidden>
          <circle cx="140" cy="100" r="52" fill={surface} />
          <circle cx="108" cy="88" r="14" fill="#f472b6" />
          <circle cx="132" cy="72" r="14" fill="#fb923c" />
          <circle cx="160" cy="76" r="14" fill="#a78bfa" />
          <circle cx="176" cy="100" r="14" fill="#34d399" />
          <circle cx="168" cy="128" r="14" fill="#60a5fa" />
          <circle cx="112" cy="124" r="14" fill="#fbbf24" />
          <rect x="88" y="148" width="104" height="12" rx="6" fill={surface} />
          <path d="M200 64 Q220 48 232 72" stroke={ink} strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <circle cx="232" cy="72" r="6" fill={accent} />
          <path d="M228 68 l8 8 M236 68 l-8 8" stroke={surface} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );

    case 'humanities':
      return (
        <svg className={shared} viewBox="0 0 280 200" fill="none" aria-hidden>
          <path d="M72 48 h136 v104 H72 Z" fill={surface} />
          <path d="M88 48 v104" stroke={inkMuted} strokeWidth="2" />
          <rect x="104" y="72" width="88" height="6" rx="3" fill={inkMuted} fillOpacity="0.35" />
          <rect x="104" y="88" width="72" height="6" rx="3" fill={inkMuted} fillOpacity="0.25" />
          <rect x="104" y="104" width="80" height="6" rx="3" fill={inkMuted} fillOpacity="0.25" />
          <rect x="104" y="120" width="56" height="6" rx="3" fill={inkMuted} fillOpacity="0.2" />
          <path d="M168 152 Q140 168 112 152" stroke={accent} strokeWidth="3" fill="none" strokeLinecap="round" />
          <circle cx="196" cy="56" r="20" fill={accent} fillOpacity="0.2" />
          <text x="188" y="62" fill={ink} fontSize="16" fontWeight="700">
            文
          </text>
        </svg>
      );

    case 'social':
      return (
        <svg className={shared} viewBox="0 0 280 200" fill="none" aria-hidden>
          <circle cx="96" cy="88" r="28" fill={surface} />
          <circle cx="184" cy="88" r="28" fill={surface} />
          <path d="M68 128 Q96 148 124 128" stroke={ink} strokeWidth="2.5" fill="none" />
          <path d="M156 128 Q184 148 212 128" stroke={ink} strokeWidth="2.5" fill="none" />
          <path d="M124 100 Q140 76 156 100" stroke={accent} strokeWidth="3" fill="none" strokeLinecap="round" />
          <circle cx="88" cy="84" r="4" fill={ink} />
          <circle cx="104" cy="84" r="4" fill={ink} />
          <path d="M92 96 Q96 100 100 96" stroke={ink} strokeWidth="2" fill="none" strokeLinecap="round" />
          <circle cx="176" cy="84" r="4" fill={ink} />
          <circle cx="192" cy="84" r="4" fill={ink} />
          <path d="M180 96 Q184 100 188 96" stroke={ink} strokeWidth="2" fill="none" strokeLinecap="round" />
          <path
            d="M132 156 Q140 172 148 156"
            stroke={accent}
            strokeWidth="2.5"
            fill={accent}
            fillOpacity="0.15"
          />
        </svg>
      );

    case 'health':
      return (
        <svg className={shared} viewBox="0 0 280 200" fill="none" aria-hidden>
          <rect x="88" y="56" width="104" height="112" rx="20" fill={surface} />
          <circle cx="140" cy="96" r="32" fill={accent} fillOpacity="0.15" />
          <path d="M140 80 v32 M124 96 h32" stroke={accent} strokeWidth="6" strokeLinecap="round" />
          <path
            d="M56 120 Q88 96 120 120 T184 120 T248 120"
            stroke={inkMuted}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
          <circle cx="64" cy="120" r="8" fill={surface} stroke={ink} strokeWidth="2" />
          <circle cx="216" cy="120" r="8" fill={surface} stroke={ink} strokeWidth="2" />
          <rect x="120" y="148" width="40" height="24" rx="12" fill={accent} fillOpacity="0.25" />
        </svg>
      );

    default:
      return (
        <svg className={shared} viewBox="0 0 280 200" fill="none" aria-hidden>
          <circle cx="140" cy="100" r="56" fill={surface} stroke={inkMuted} strokeWidth="3" />
          <path d="M140 56 L156 100 L140 144 L124 100 Z" fill={accent} fillOpacity="0.3" />
          <circle cx="140" cy="100" r="12" fill={accent} />
          <path d="M140 44 v16 M140 140 v16 M84 100 h16 M180 100 h16" stroke={inkMuted} strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      );
  }
}

export default InterestClusterIllustration;
